import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';

import { Button, InfoTipIcon, RevelioTooltip, Text } from '@revelio/ui';

import {
  CircleCheckbox,
  type DeliverableIcons,
  DeliverablesIconLookup,
} from '../icons';
import cardStyles from './card-list-select.module.css';

export interface ICardListSelectProps<T> {
  id: T;
  /** Controlled ticked state. Without it the card keeps its own, flipped on
   *  each click. */
  isChecked?: boolean;
  isDisabled?: boolean;
  /** Adds the "*" after the title (also set by `isMandatory`). */
  isRequired?: boolean;
  /** Fires on every click of the select area, with the hidden input's change
   *  event (`event.target.checked` is the next state). */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  borderColor?: string;
  label?: string;
  hideCheckbox?: boolean;
  combineWith?: T[]; // unused in component, it is config for adding multiple columns based on single column toggle
  icon?: DeliverableIcons | string;
  detail?: string;
  cta?: JSX.Element;
  hideCtaIfNotSelected?: boolean;
  disabledExplanation?: string;
  variant?: string;
  defaultIsSelected?: boolean;
  isMandatory?: boolean;
  internalOnly?: boolean;
  columnFileSize?: number;
  rowPossibilities?: number;
  tooltipInfo?: string;
}

/** The round tick's size per card variant; other variants use 22px. */
const CHECK_SIZE_BY_VARIANT: Record<string, string> = {
  dataset: '20px',
  delivery: '20px',
  columns: '15px',
};

export function CardListSelect<T extends string>({
  id,
  label,
  detail,
  cta,
  hideCtaIfNotSelected = false,
  icon,
  variant,
  hideCheckbox = false,
  borderColor,
  disabledExplanation,
  defaultIsSelected: _defaultIsSelected, // destructured so it's not passed to the checkbox in DOM
  isMandatory,
  tooltipInfo,
  isChecked: isCheckedProp,
  isDisabled: isDisabledProp,
  isRequired,
  onChange,
  value,
}: ICardListSelectProps<T>) {
  const checkSize = CHECK_SIZE_BY_VARIANT[variant ?? 'dataset'] ?? '22px';

  const textRef = useRef<HTMLParagraphElement>(null);
  const maxLinesCount: 1 | 2 = variant === 'report' ? 1 : 2;
  const allowShowMore = variant !== 'report';
  const [detailIsOverflowing, setDetailIsOverflowing] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const checkOverflow = () => {
      const current = textRef.current;
      if (current && current?.scrollHeight > current?.clientHeight) {
        setDetailIsOverflowing(true);
      } else {
        setDetailIsOverflowing(false);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [detail, variant]);

  const [maxLines, setMaxLines] = useState<0 | 1 | 2>(maxLinesCount);
  const handleShowMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMaxLines((v) => (v == 0 ? maxLinesCount : 0));
  };

  const fmtLabel = label || capitalize(id);

  const TheIcon = get(DeliverablesIconLookup, `${icon}`);

  const inputRef = useRef<HTMLInputElement>(null);
  const [ownIsChecked, setOwnIsChecked] = useState(false);
  const state = {
    isChecked: isCheckedProp ?? ownIsChecked,
    isDisabled: isMandatory || !!isDisabledProp,
    isRequired: !!isMandatory || !!isRequired,
  };
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isCheckedProp === undefined) setOwnIsChecked(event.target.checked);
    onChange?.(event);
  };
  const ctaInCardBody = cta && variant === 'dataset';
  const canShowCTA = !(hideCtaIfNotSelected && !state.isChecked);

  // Theming is scoped to the report-builder "report" variant and the
  // data-enrichment / sheet-selection "columns" variant — the class and
  // data-attrs are not applied for any other variant, so the remaining shared
  // usages (dataset/delivery) are unaffected.
  const isReportVariant = variant === 'report' || variant === 'columns';

  const CardSelect = (
    <div
      className={classNames(
        cardStyles.card,
        isReportVariant && cardStyles['reportCard']
      )}
      data-variant={variant ?? 'dataset'}
      data-border={
        borderColor || state.isDisabled
          ? 'muted'
          : // biome-ignore lint/style/noNestedTernary: migrated from develop
            state.isChecked
            ? 'checked'
            : 'default'
      }
      data-locked={state.isDisabled || undefined}
      data-checked={isReportVariant ? String(state.isChecked) : undefined}
      data-disabled={isReportVariant ? String(state.isDisabled) : undefined}
      role="group"
    >
      <div
        className={cardStyles.cardRow}
        data-align-top={variant === 'dataset' || undefined}
        data-has-icon={icon ? true : undefined}
        data-locked={state.isDisabled || undefined}
      >
        {/* Clicking anywhere in the select area toggles the hidden input; a
            disabled input ignores the click. */}
        <div
          className={cardStyles.selectArea}
          onClick={(event) => {
            if (event.target !== inputRef.current) inputRef.current?.click();
          }}
        >
          <input
            ref={inputRef}
            type="checkbox"
            hidden
            checked={state.isChecked}
            disabled={state.isDisabled}
            required={state.isRequired}
            value={value}
            onChange={handleChange}
          />
          <div
            className={cardStyles.checkSlot}
            data-report-radio={variant === 'report' ? '' : undefined}
          >
            {!hideCheckbox && (
              <CircleCheckbox
                height={checkSize}
                width={checkSize}
                checked={state.isChecked}
                isDisabled={state.isDisabled}
                themed={isReportVariant}
              />
            )}
          </div>
          {icon && TheIcon && (
            <div
              className={cardStyles.iconBadge}
              data-locked={state.isDisabled || undefined}
            >
              <div className={cardStyles.iconWrapper}>
                <TheIcon size="1.5rem" />
              </div>
            </div>
          )}
          <div className={cardStyles.cardContent}>
            <div className={cardStyles.cardHeader}>
              <div className={cardStyles.titleRow}>
                <Text
                  as="h2"
                  fontSize={variant === 'columns' ? 'sm' : 'md'}
                  weight={variant === 'delivery' ? 'semibold' : 'regular'}
                  className={classNames(
                    cardStyles.title,
                    isReportVariant && cardStyles['reportTitle']
                  )}
                  data-locked={state.isDisabled || undefined}
                  data-disabled={
                    isReportVariant ? String(state.isDisabled) : undefined
                  }
                >
                  {fmtLabel}
                  {state.isRequired && '*'}
                </Text>
                {tooltipInfo ? (
                  <RevelioTooltip
                    label={tooltipInfo}
                    textAlign="center"
                    placement="top"
                  >
                    <InfoTipIcon size={12} />
                  </RevelioTooltip>
                ) : null}
              </div>
            </div>
            {detail && (
              <div
                className={cardStyles.cardBody}
                data-cta-inside={ctaInCardBody || undefined}
              >
                <Text
                  fontSize="x-sm"
                  lineClamp={maxLines || undefined}
                  className={classNames(
                    cardStyles.detail,
                    isReportVariant && cardStyles['reportDetail']
                  )}
                  data-locked={state.isDisabled || undefined}
                  data-disabled={
                    isReportVariant ? String(state.isDisabled) : undefined
                  }
                  ref={textRef}
                  title={detailIsOverflowing ? detail : undefined}
                >
                  {detail}
                </Text>
                <div className={cardStyles.detailFooter}>
                  <div>
                    {allowShowMore && detailIsOverflowing && (
                      <Button
                        size="xs"
                        variant="link"
                        color="lightBlue"
                        onClick={handleShowMore}
                        disabled={state.isDisabled}
                      >
                        {maxLines === 0 ? 'less' : 'more'}
                      </Button>
                    )}
                  </div>

                  {canShowCTA && ctaInCardBody && (
                    <div
                      className={cardStyles.bodyCta}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {cta}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {canShowCTA && !ctaInCardBody && cta}
      </div>
    </div>
  );

  if (disabledExplanation) {
    return (
      <RevelioTooltip label={disabledExplanation} fullWidthTrigger>
        {CardSelect}
      </RevelioTooltip>
    );
  }

  return CardSelect;
}
